/** Path helpers for the audio library's implied-folder tree. Paths are
 * normalized "A/B/C" strings ('' = root), matching the backend's
 * normalize_folder output. */

export function parentOf(path: string): string {
  const i = path.lastIndexOf('/');
  return i === -1 ? '' : path.slice(0, i);
}

export function baseName(path: string): string {
  const i = path.lastIndexOf('/');
  return i === -1 ? path : path.slice(i + 1);
}

export function joinPath(parent: string, child: string): string {
  return parent ? `${parent}/${child}` : child;
}

/** Immediate subfolders of `path` from a flat list of all folder paths. */
export function childFolders(all: string[], path: string): string[] {
  return all.filter((f) => parentOf(f) === path && f !== path).sort();
}

/** Expand asset folder values into the full implied set (an asset in
 * "A/B" implies "A" exists). Mirrors the backend's folders endpoint
 * for callers that already hold every asset (the wizard picker). */
export function impliedFolders(folderValues: string[]): string[] {
  const out = new Set<string>();
  for (const folder of folderValues) {
    if (!folder) continue;
    const parts = folder.split('/');
    for (let i = 1; i <= parts.length; i += 1) {
      out.add(parts.slice(0, i).join('/'));
    }
  }
  return Array.from(out).sort();
}

const AUDIO_EXTENSIONS = /\.(mp3|m4a|m4b|aac|wav|ogg|oga|opus|flac|wma|aif|aiff)$/i;

/** Directory uploads sweep in junk (.DS_Store, cover art, etc.) —
 * keep only files that look like audio. Extension check runs even when
 * the browser reports a non-audio MIME (.m4a often comes back as
 * video/mp4). */
export function isAudioFile(file: File): boolean {
  return file.type.startsWith('audio/') || AUDIO_EXTENSIONS.test(file.name);
}
